import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Progress } from './progress';

const notificationToastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-8 shadow-lg transition-all',
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        success: 'border-green-500/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-50',
        error: 'border-red-500/50 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-50',
        warning: 'border-yellow-500/50 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-50',
        info: 'border-blue-500/50 bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface NotificationToastProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof notificationToastVariants> {
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  onClose?: () => void;
  progress?: number;
  showProgress?: boolean;
  estimatedTime?: string;
  dismissible?: boolean;
}

const NotificationToast = React.forwardRef<HTMLDivElement, NotificationToastProps>(
  ({
    className,
    variant = 'default',
    title,
    description,
    action,
    onClose,
    progress,
    showProgress = false,
    estimatedTime,
    dismissible = true,
    ...props
  }, ref) => {
    const getIcon = () => {
      switch (variant) {
        case 'success':
          return <CheckCircle className="h-5 w-5 text-green-600" />;
        case 'error':
          return <AlertCircle className="h-5 w-5 text-red-600" />;
        case 'warning':
          return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
        case 'info':
          return <Info className="h-5 w-5 text-blue-600" />;
        default:
          return null;
      }
    };

    return (
      <div
        ref={ref}
        className={cn(notificationToastVariants({ variant, className }))}
        {...props}
      >
        <div className="flex items-start space-x-3 flex-1">
          {getIcon()}
          
          <div className="flex-1 space-y-1">
            {title && (
              <div className="text-sm font-semibold leading-none tracking-tight">
                {title}
              </div>
            )}
            
            {description && (
              <div className="text-sm opacity-90">
                {description}
              </div>
            )}
            
            {showProgress && progress !== undefined && (
              <div className="space-y-1 pt-2">
                <Progress 
                  value={progress} 
                  size="sm"
                  variant={variant === 'error' ? 'error' : variant === 'warning' ? 'warning' : variant === 'success' ? 'success' : 'default'}
                />
                <div className="flex justify-between text-xs opacity-75">
                  <span>{Math.round(progress)}% complete</span>
                  {estimatedTime && <span>ETA: {estimatedTime}</span>}
                </div>
              </div>
            )}
            
            {action && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={action.onClick}
                  className="h-7 text-xs"
                >
                  {action.label}
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {dismissible && onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute right-2 top-2 h-6 w-6 p-0 opacity-70 hover:opacity-100"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        )}
      </div>
    );
  }
);
NotificationToast.displayName = 'NotificationToast';

export { NotificationToast, notificationToastVariants };